import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, Box, IconButton } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import FormWrapper from '../Forms/FormWrapper';
import { PasswordField } from '../PasswordField';
import CustomSnackbar from '../snackbar/CustomSnackbar';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import {ChangePasswordSchema} from '../../Validation/ValidationRule';
import handleLogout from '../handleLogout';
import styles from '../../styles/profile/index.module.scss';
import { changePassword } from '../../utils/api/user_apis';
import { STATUS } from '../../utils/api/sharedfunction';

const ChangePassword = () => {
  const auth = useSelector((state) => state.auth);
  const user = auth.user;
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const [name] = useState(user?.username);
  const [email] = useState(user?.user_email);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data) => {
    try {
      const response = await changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      if (response.status === STATUS.SUCCESS) {
        setMessage(response.message || 'Password changed successfully');
        setSeverity('success');
        reset();
        setSnackbarOpen(true);
        setTimeout(() => {
          handleLogout(navigate, dispatch);
        }, 1500);
      } else {
        setMessage(response.error || 'Failed to change password');
        setSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
      setMessage(error.error || 'Failed to change password. Please try again.');
      setSeverity('error');
      setSnackbarOpen(true);
    }
  };

     // Close Snackbar
     const handleSnackbarClose = () => {
      setSnackbarOpen(false);
    };
  

  return (
    <Box className={styles.container}>
      <div className={styles.headerContainer}>
        <IconButton
          onClick={() => navigate('/')}
          className={styles.homeButton}
        >
          <HomeIcon className={styles.homeIcon} />
        </IconButton>
      </div>
      <FormWrapper
        title="User Profile"
        onSubmit={handleSubmit(onSubmit)}
        loading={false}
        error={null}
        buttonName="Change Password"
        submitDisabled={!isValid}
      >
        <TextField name='name' label="Name" value={name} disabled fullWidth margin="normal" />
        <TextField name='email' label="Email" value={email} disabled fullWidth margin="normal" />
        <Controller
          name="currentPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              field={field}
              label="Current Password"
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
            />
          )}
        />
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              field={field}
              label="New Password"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Confirm Password"
              type="password"
              fullWidth
              margin="normal"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />
          )}
        />
        {/* Snackbar */}
        <CustomSnackbar
          open={snackbarOpen}
          message={message}
          severity={severity}
          onClose={handleSnackbarClose}
        />
      </FormWrapper>
    </Box>
  );
};

export default ChangePassword;